import { useState } from 'react'
import { Link } from 'react-router'
import {
  ALL_GARMENTS,
  continueOrderUrl,
  editOrderUrl,
  formatDate,
  formatDateMonthDay,
} from '@trinity/utils'
import { ImageNotFound } from '../../assets'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ArrowForward,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  List,
  ListItem,
  Popover,
  Stack,
  StatusChip,
  Typography,
  Warning,
} from '../../components'
import { isGarmentDelayed, useOrderStatus } from './OrderStatusProvider'

interface OrderProps {
  order: TrinityAPI.OrderHomeType
  children: React.ReactNode
}

export function OrderStatusAccordion({ order, children }: OrderProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  return (
    <>
      <Accordion>
        <AccordionSummary>{children}</AccordionSummary>
        <AccordionDetails sx={{ p: 0, bgcolor: 'grey.50' }}>
          <AccordionDetailsHeader order={order} />
          {order.garments.map(garment => (
            <AccordionDetailsBody key={garment.id} garment={garment} setAnchorEl={setAnchorEl} />
          ))}
          <AccordionDetailsFooter order={order} />
        </AccordionDetails>
      </Accordion>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={() => setAnchorEl(null)}
      >
        <Stack spacing={2} maxWidth={400} p={2} sx={{ overflowY: { mobile: 'scroll', laptop: 'auto' } }}>
          <Typography variant='body1'>
            <strong>About Estimated Ship Dates</strong>
          </Typography>
          <Typography variant='smallBody1'>
            Estimated ship dates reflect the typical time required to produce and ship each garment type.
          </Typography>
          <Typography variant='smallBody1'>
            These dates describe when items shipped <i>from from the factory</i> and are based on our historical
            averages and current production schedules.
          </Typography>
          <Typography variant='smallBody1'>
            <strong>Please Note:</strong>
          </Typography>
          <List
            sx={{ listStyleType: 'disc', pl: 2, '& .MuiListItem-root': { display: 'list-item' }, color: 'grey.300' }}
          >
            <ListItem>
              <Typography variant='smallBody2'>
                Dates are estimates, not guarantees. Actual delivery may vary due to fabric availability, customization
                complexity, or unexpected production delays.
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant='smallBody2'>
                You will receive updates if your order status changes or if there are any delays.
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant='smallBody2'>
                Tracking information will be provided as soon as your order ships
              </Typography>
            </ListItem>
          </List>
        </Stack>
      </Popover>
    </>
  )
}

function AccordionDetailsHeader({ order }: Pick<OrderProps, 'order'>) {
  const { onMobile, INCOMPLETE, currentScope } = useOrderStatus()
  if (onMobile) {
    return (
      <Stack spacing={3} py={3} alignItems='center' justifyContent='center'>
        <Typography variant='h5'>{order.title}</Typography>
        <Divider sx={{ width: 1 }} />
      </Stack>
    )
  }

  return (
    <Stack spacing={2}>
      <Stack px={4} py={1} direction='row' sx={{ backgroundColor: 'grey.100' }}>
        <Typography variant='htable' sx={{ flex: 1 }}>
          GARMENT ID#
        </Typography>
        <Typography variant='htable' sx={{ flex: 1.75 }}>
          FABRIC
        </Typography>
        <Stack direction='row' justifyContent='center' sx={{ flex: 1 }}>
          {currentScope !== INCOMPLETE && <Typography variant='htable'>STATUS</Typography>}
        </Stack>
        <Box sx={{ flex: 1.75 }}></Box>
      </Stack>
    </Stack>
  )
}

function AccordionDetailsBody({
  garment,
  setAnchorEl,
}: {
  garment: TrinityAPI.OrderHomeGarmentType
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>
}) {
  const { onMobile, INCOMPLETE, currentScope } = useOrderStatus()
  const garmentName = ALL_GARMENTS.find(g => g.abbreviation === garment.garmentType)?.formalName
  const estimatedShipDateRange = getEstimatedShipDateRange(garment.estimatedShipDate)

  return (
    <Stack
      direction={onMobile ? 'column' : 'row'}
      alignItems='center'
      spacing={{ mobile: 2, laptop: 0 }}
      sx={{ px: 4, py: { mobile: 4, laptop: 0 }, borderBottom: 'n100' }}
    >
      <Box sx={{ width: { mobile: 1 }, flex: 1 }}>
        <Typography variant='h6'>{garment.title}</Typography>
        <Typography variant='smallBody1'>{garmentName}</Typography>
      </Box>
      <FabricDetails fabric={garment.fabric} />
      {currentScope !== INCOMPLETE && (
        <>
          <Stack pt={0} width={{ mobile: 1 }} alignItems={{ laptop: 'center' }} flex={1}>
            <StatusChip status={garment.orderStatus.description} sx={{ width: 'fit-content' }} />
            <Typography variant='smallBody1' sx={{ pl: { mobile: 1, laptop: 0 } }}>
              {formatDate(new Date(garment.statusUpdatedAt))}
            </Typography>
          </Stack>
          <Stack
            spacing={1}
            alignItems={{ laptop: 'center' }}
            width={{ mobile: 1, laptop: 'auto' }}
            sx={{ display: 'flex', flex: 1 }}
          >
            {estimatedShipDateRange && (
              <>
                <Typography variant='smallBody1' color='grey.900'>
                  <Typography
                    onClick={e => setAnchorEl(e.currentTarget)}
                    sx={{ textDecoration: 'underline', cursor: 'pointer', color: 'primary.main' }}
                  >
                    ESD - Factory
                  </Typography>
                </Typography>
                <Typography variant='smallBody1'>{estimatedShipDateRange}</Typography>
              </>
            )}
          </Stack>
        </>
      )}
      <Box sx={{ width: { mobile: 1, laptop: 'auto' }, flex: 0.75, textAlign: { mobile: 'left', laptop: 'right' } }}>
        <AccordionDetailsBodyAction garment={garment} />
      </Box>
    </Stack>
  )
}

function AccordionDetailsFooter({ order }: Pick<OrderProps, 'order'>) {
  const { DELAYED, INCOMPLETE, currentScope, onMobile } = useOrderStatus()

  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent={{ mobile: 'flex-start', laptop: 'space-between' }}
      sx={{ p: 3 }}
    >
      {!onMobile && <Typography variant='h6'>{order.title}</Typography>}
      {currentScope === DELAYED && (
        <Chip
          icon={<Warning />}
          label={`${order.garments.filter(isGarmentDelayed).length} of ${order.garmentCount} items have a delay.`}
          sx={{
            display: { mobile: 'none', laptop: 'flex' },
            backgroundColor: 'error.extraLight',
            borderRadius: 0,
            p: 3,
            '& .MuiChip-icon': { color: 'error.main' },
            '& .MuiChip-label': { color: 'error.main' },
          }}
        />
      )}
      {currentScope === INCOMPLETE ? (
        <Button
          variant='outlined'
          href={continueOrderUrl(order.id)}
          target='_blank'
          rel='noreferrer'
          endIcon={<ArrowForward />}
        >
          Continue Order
        </Button>
      ) : (
        <Button variant='text' component={Link} to={`/orders/${order.id}`}>
          View Order Details
        </Button>
      )}
    </Stack>
  )
}

//* Abstractions to help clean up AccordionDetailsBody a bit
function FabricDetails({ fabric }: { fabric: TrinityAPI.FabricHomeType }) {
  const { trinityFabricNumber, id, description, url } = fabric

  return (
    <Card sx={{ width: { mobile: 1, laptop: 2 / 5 }, flex: 1.75, bgcolor: 'grey.50', boxShadow: 0 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { mobile: 'column', laptop: 'row' },
          alignItems: { mobile: 'flex-start', laptop: 'center' },
        }}
      >
        <CardMedia
          component='img'
          image={url}
          alt={trinityFabricNumber}
          sx={{
            border: 'n100',
            height: { mobile: 140, laptop: 65 },
            width: { mobile: 288, laptop: 66 },
          }}
          onError={e => (e.currentTarget.src = ImageNotFound)}
        />
        <CardContent sx={{ pl: { mobile: 0, laptop: 2 } }}>
          <Typography
            gutterBottom
            variant='link'
            component={Link}
            to={`/fabric-explorer/fabrics/${id}`}
            target='_blank'
          >
            {trinityFabricNumber}
          </Typography>
          <Typography
            noWrap
            variant='smallBody1'
            component='div'
            sx={{ maxWidth: { laptop: 100, desktop: 180 }, mt: 1 }}
            title={description ?? ''}
          >
            {description}
          </Typography>
        </CardContent>
      </Box>
    </Card>
  )
}

function AccordionDetailsBodyAction({ garment }: { garment: TrinityAPI.OrderHomeGarmentType }) {
  const { getOrderStatus, DELAYED, EDITABLE, DELIVERY, onSkiffle, currentScope } = useOrderStatus()
  const orderStatus = getOrderStatus(currentScope, garment)

  if (currentScope === DELAYED || orderStatus === DELAYED) {
    return isGarmentDelayed(garment) ? (
      <Typography variant='smallBody1' color='error'>
        {garment.delayStatus.description}
      </Typography>
    ) : null
  }

  if (orderStatus === EDITABLE && !onSkiffle) {
    return (
      <Button variant='outlined' href={editOrderUrl(garment.orderId, garment.id)} target='_blank' rel='noreferrer'>
        Edit
      </Button>
    )
  }

  if (orderStatus === DELIVERY && garment?.shipment?.trackingLink) {
    return (
      <Button
        variant='outlined'
        href={garment.shipment.trackingLink}
        target='_blank'
        rel='noreferrer'
        title={`${garment.shipment.trackingNumber} via ${garment.shipment.carrier}`}
      >
        Track
      </Button>
    )
  }

  return null
}

function getEstimatedShipDateRange(orderDate: Date | null, buffer = 2) {
  if (orderDate === null) {
    return null
  }
  if (buffer < 0) {
    buffer = 0
  }
  let lowerBound: string | null = null
  let upperBound: string | null = null

  const date = new Date(orderDate)

  if (buffer > 0) {
    const lowerDate = new Date(date)
    lowerDate.setDate(lowerDate.getDate() - buffer)
    lowerBound = formatDateMonthDay(lowerDate)
    const higherDate = new Date(date)
    higherDate.setDate(higherDate.getDate() + buffer)
    upperBound = formatDateMonthDay(higherDate)
  }

  if (lowerBound === null && upperBound === null) {
    return formatDateMonthDay(date)
  }

  return `${lowerBound} - ${upperBound}`
}
